import { checkIsLoop } from "@/core/util";
import { getLoopEndOrigId } from "@/flow/utils/loopUtils";

export const STRUCTURE_HORIZONTAL_GAP = 200;
export const ROUTE_VERTICAL_GAP = 200;

export type StructureRange = {
  startIndex: number;
  endIndex: number;
};

export type Bounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

export type ListContext = {
  list: any[];
  parent: ListContext | null;
  parentIndex: number;
  parentList: any[] | null;
  parentRouter: any | null;
  route: any | null;
  routeIndex: number;
};

export type ShiftScope = {
  movableOrigIds?: Set<string> | null;
};

const getNumericPosition = (value: any, fallback: number): number =>
  typeof value === "number" && !Number.isNaN(value) ? value : fallback;

const getStepOrigId = (step: any): string =>
  step?.origId !== undefined && step?.origId !== null
    ? String(step.origId)
    : "";

const isStepInShiftScope = (
  step: any,
  movableOrigIds?: Set<string> | null
): boolean => {
  if (!movableOrigIds) return true;

  const origId = getStepOrigId(step);
  return Boolean(origId && movableOrigIds.has(origId));
};

const filterStepsByShiftScope = (
  steps: any[],
  movableOrigIds?: Set<string> | null
): any[] =>
  movableOrigIds
    ? steps.filter((step) => isStepInShiftScope(step, movableOrigIds))
    : steps;

const getScopedListBounds = (
  list: any[],
  movableOrigIds?: Set<string> | null
): Bounds | null => {
  if (!movableOrigIds) return getListBounds(list);

  const visited = new Set<any>();
  const collected: any[] = [];

  list.forEach((step) => collectStepWithDescendants(step, collected, visited));

  return getStepsBounds(filterStepsByShiftScope(collected, movableOrigIds));
};

const getScopedRouteBranchSteps = (
  route: any,
  movableOrigIds?: Set<string> | null
): any[] =>
  filterStepsByShiftScope(collectRouteBranchSteps(route), movableOrigIds);

const getStepPosition = (step: any) => ({
  x: getNumericPosition(
    step?.position?.x,
    getNumericPosition(step?.ix, 0) * 200
  ),
  y: getNumericPosition(
    step?.position?.y,
    getNumericPosition(step?.iy, 0) * 200
  ),
});

const hasVerticalOverlap = (first: Bounds, second: Bounds): boolean =>
  first.minY <= second.maxY && second.minY <= first.maxY;

const collectStepWithDescendants = (
  step: any,
  collected: any[],
  visited: Set<any>
) => {
  if (!step || visited.has(step)) return;

  visited.add(step);
  collected.push(step);

  if (step?.type === "ROUTER" && Array.isArray(step.routes)) {
    step.routes.forEach((route: any) => {
      (route?.steps || []).forEach((routeStep: any) =>
        collectStepWithDescendants(routeStep, collected, visited)
      );
    });
  }
};

export function getStructureRange(list: any[], index: number): StructureRange {
  if (!Array.isArray(list) || index < 0 || index >= list.length) {
    return { startIndex: index, endIndex: index };
  }

  const step = list[index];
  if (!(checkIsLoop(step) || step?.type === "LOOP")) {
    return { startIndex: index, endIndex: index };
  }

  const loopEndOrigId = getLoopEndOrigId(step);
  if (!loopEndOrigId) {
    return { startIndex: index, endIndex: index };
  }

  const endIndex = list.findIndex(
    (candidate: any, candidateIndex: number) =>
      candidateIndex >= index && candidate?.origId === loopEndOrigId
  );

  return {
    startIndex: index,
    endIndex: endIndex === -1 ? index : endIndex,
  };
}

export function collectStructureSteps(
  list: any[],
  range: StructureRange
): any[] {
  if (!Array.isArray(list) || !list.length) return [];

  const startIndex = Math.max(0, range.startIndex);
  const endIndex = Math.min(list.length - 1, range.endIndex);
  const visited = new Set<any>();
  const collected: any[] = [];

  for (
    let currentIndex = startIndex;
    currentIndex <= endIndex;
    currentIndex += 1
  ) {
    collectStepWithDescendants(list[currentIndex], collected, visited);
  }

  return collected;
}

const collectRouteBranchSteps = (route: any): any[] => {
  const steps = Array.isArray(route?.steps) ? route.steps : [];
  if (!steps.length) return [];

  return collectStructureSteps(steps, {
    startIndex: 0,
    endIndex: steps.length - 1,
  });
};

export function getStepsBounds(steps: any[]): Bounds | null {
  if (!Array.isArray(steps) || !steps.length) return null;

  const bounds = steps.reduce((acc: Bounds | null, step: any) => {
    const position = getStepPosition(step);
    if (!acc) {
      return {
        minX: position.x,
        maxX: position.x,
        minY: position.y,
        maxY: position.y,
      };
    }

    return {
      minX: Math.min(acc.minX, position.x),
      maxX: Math.max(acc.maxX, position.x),
      minY: Math.min(acc.minY, position.y),
      maxY: Math.max(acc.maxY, position.y),
    };
  }, null);

  return bounds;
}

export function getListBounds(list: any[]): Bounds | null {
  if (!Array.isArray(list) || !list.length) return null;

  const visited = new Set<any>();
  const collected: any[] = [];

  list.forEach((step) => collectStepWithDescendants(step, collected, visited));

  return getStepsBounds(collected);
}

export function shiftStructureSteps(
  steps: any[],
  deltaX = 0,
  deltaY = 0,
  movableOrigIds?: Set<string> | null
): void {
  if (!Array.isArray(steps) || (!deltaX && !deltaY)) return;

  filterStepsByShiftScope(steps, movableOrigIds).forEach((step) => {
    const position = getStepPosition(step);
    step.position = {
      x: position.x + deltaX,
      y: position.y + deltaY,
    };
  });
}

export function shiftLinearSiblingStructuresRight(
  list: any[],
  insertedRange: StructureRange,
  gap = STRUCTURE_HORIZONTAL_GAP,
  movableOrigIds?: Set<string> | null
): void {
  if (!Array.isArray(list) || !list.length) return;

  const insertedSteps = collectStructureSteps(list, insertedRange);
  const insertedBounds = getStepsBounds(insertedSteps);
  if (!insertedBounds) return;

  let cursor = insertedBounds.maxX + gap;
  let currentIndex = insertedRange.endIndex + 1;

  while (currentIndex < list.length) {
    const structureRange = getStructureRange(list, currentIndex);
    const structureSteps = filterStepsByShiftScope(
      collectStructureSteps(list, structureRange),
      movableOrigIds
    );
    const structureBounds = getStepsBounds(structureSteps);

    if (structureBounds) {
      if (structureBounds.minX < cursor) {
        const deltaX = cursor - structureBounds.minX;
        shiftStructureSteps(structureSteps, deltaX, 0, movableOrigIds);
        cursor = structureBounds.maxX + deltaX + gap;
      } else {
        cursor = structureBounds.maxX + gap;
      }
    }

    currentIndex = structureRange.endIndex + 1;
  }
}

export function shiftLinearSiblingStructuresLeft(
  list: any[],
  startIndex: number,
  cursorX: number,
  gap = STRUCTURE_HORIZONTAL_GAP,
  movableOrigIds?: Set<string> | null
): void {
  if (!Array.isArray(list) || !list.length || startIndex >= list.length) return;

  let cursor = cursorX;
  let currentIndex = Math.max(0, startIndex);

  while (currentIndex < list.length) {
    const structureRange = getStructureRange(list, currentIndex);
    const structureSteps = filterStepsByShiftScope(
      collectStructureSteps(list, structureRange),
      movableOrigIds
    );
    const structureBounds = getStepsBounds(structureSteps);

    if (structureBounds) {
      if (structureBounds.minX > cursor) {
        const deltaX = cursor - structureBounds.minX;
        shiftStructureSteps(structureSteps, deltaX, 0, movableOrigIds);
        cursor = structureBounds.maxX + deltaX + gap;
      } else {
        cursor = structureBounds.maxX + gap;
      }
    }

    currentIndex = structureRange.endIndex + 1;
  }
}

export function shiftIndependentSiblingBranchesRight(
  routerStep: any,
  activeRouteIndex: number,
  anchorBounds: Bounds,
  gap = STRUCTURE_HORIZONTAL_GAP,
  movableOrigIds?: Set<string> | null
): void {
  if (!routerStep || !Array.isArray(routerStep.routes) || !anchorBounds) return;

  const siblingRoutes = routerStep.routes
    .map((route: any, index: number) => ({
      bounds: getScopedListBounds(route?.steps || [], movableOrigIds),
      index,
      route,
    }))
    .filter(
      ({ bounds, index }) =>
        index > activeRouteIndex &&
        bounds &&
        bounds.minX >= anchorBounds.minX &&
        hasVerticalOverlap(bounds, anchorBounds)
    )
    .sort((first, second) => first.bounds.minX - second.bounds.minX);

  let cursor = anchorBounds.maxX + gap;

  siblingRoutes.forEach(({ bounds, route }) => {
    if (bounds.minX < cursor) {
      const deltaX = cursor - bounds.minX;
      shiftStructureSteps(
        getScopedRouteBranchSteps(route, movableOrigIds),
        deltaX,
        0,
        movableOrigIds
      );
      cursor = bounds.maxX + deltaX + gap;
      return;
    }

    cursor = bounds.maxX + gap;
  });
}

export function shiftIndependentSiblingBranchesLeft(
  routerStep: any,
  activeRouteIndex: number,
  anchorBounds: Bounds,
  gap = STRUCTURE_HORIZONTAL_GAP,
  movableOrigIds?: Set<string> | null
): void {
  if (!routerStep || !Array.isArray(routerStep.routes) || !anchorBounds) return;

  const siblingRoutes = routerStep.routes
    .map((route: any, index: number) => ({
      bounds: getScopedListBounds(route?.steps || [], movableOrigIds),
      index,
      route,
    }))
    .filter(
      ({ bounds, index }) =>
        index !== activeRouteIndex &&
        bounds &&
        bounds.minX >= anchorBounds.minX &&
        hasVerticalOverlap(bounds, anchorBounds)
    )
    .sort((first, second) => first.bounds.minX - second.bounds.minX);

  let cursor = anchorBounds.maxX + gap;

  siblingRoutes.forEach(({ bounds, route }) => {
    if (bounds.minX > cursor) {
      const deltaX = cursor - bounds.minX;
      shiftStructureSteps(
        getScopedRouteBranchSteps(route, movableOrigIds),
        deltaX,
        0,
        movableOrigIds
      );
      cursor = bounds.maxX + deltaX + gap;
      return;
    }

    cursor = Math.max(cursor, bounds.maxX + gap);
  });
}

export function compactRouterSiblingBranchesLeft(
  routerStep: any,
  gap = STRUCTURE_HORIZONTAL_GAP,
  movableOrigIds?: Set<string> | null
): void {
  if (!routerStep || !Array.isArray(routerStep.routes)) return;

  const routerPosition = getStepPosition(routerStep);
  const placedRoutes: Array<{ bounds: Bounds }> = [];

  routerStep.routes.forEach((route: any) => {
    const bounds = getScopedListBounds(route?.steps || [], movableOrigIds);
    if (!bounds) return;

    let cursor = routerPosition.x;
    placedRoutes.forEach((placed) => {
      if (hasVerticalOverlap(placed.bounds, bounds)) {
        cursor = Math.max(cursor, placed.bounds.maxX + gap);
      }
    });

    let nextBounds = bounds;
    if (bounds.minX > cursor) {
      const deltaX = cursor - bounds.minX;
      shiftStructureSteps(
        getScopedRouteBranchSteps(route, movableOrigIds),
        deltaX,
        0,
        movableOrigIds
      );
      nextBounds = {
        ...bounds,
        minX: bounds.minX + deltaX,
        maxX: bounds.maxX + deltaX,
      };
    }

    placedRoutes.push({ bounds: nextBounds });
  });
}

export function findListContext(
  rootSteps: any[],
  targetList: any[]
): ListContext | null {
  const rootContext: ListContext = {
    list: rootSteps,
    parent: null,
    parentIndex: -1,
    parentList: null,
    parentRouter: null,
    route: null,
    routeIndex: -1,
  };

  const visit = (context: ListContext): ListContext | null => {
    if (context.list === targetList) {
      return context;
    }

    for (let index = 0; index < context.list.length; index += 1) {
      const step = context.list[index];

      if (step?.type === "ROUTER" && Array.isArray(step.routes)) {
        for (
          let routeIndex = 0;
          routeIndex < step.routes.length;
          routeIndex += 1
        ) {
          const route = step.routes[routeIndex];
          const childContext: ListContext = {
            list: Array.isArray(route?.steps) ? route.steps : [],
            parent: context,
            parentIndex: index,
            parentList: context.list,
            parentRouter: step,
            route,
            routeIndex,
          };

          const foundContext = visit(childContext);
          if (foundContext) {
            return foundContext;
          }
        }
      }
    }

    return null;
  };

  return visit(rootContext);
}

export function applyHorizontalAddShift({
  insertedRange,
  rootSteps,
  targetList,
  gap = STRUCTURE_HORIZONTAL_GAP,
  routeGap = ROUTE_VERTICAL_GAP,
  movableOrigIds = null,
}: {
  insertedRange: StructureRange;
  rootSteps: any[];
  targetList: any[];
  gap?: number;
  routeGap?: number;
  movableOrigIds?: Set<string> | null;
}): void {
  const insertedOrigIds = collectStructureSteps(targetList, insertedRange)
    .map(getStepOrigId)
    .filter(Boolean);
  const effectiveMovableOrigIds = movableOrigIds
    ? new Set([...Array.from(movableOrigIds), ...insertedOrigIds])
    : null;

  shiftLinearSiblingStructuresRight(
    targetList,
    insertedRange,
    gap,
    effectiveMovableOrigIds
  );

  let context = findListContext(rootSteps, targetList);
  if (!context) return;

  while (context.parentRouter && context.parentList) {
    const currentRouteBounds = getScopedListBounds(
      context.list,
      effectiveMovableOrigIds
    );

    if (currentRouteBounds) {
      shiftRouteSiblingBranchesDown(
        context.parentRouter,
        context.routeIndex,
        routeGap,
        effectiveMovableOrigIds
      );
      shiftIndependentSiblingBranchesRight(
        context.parentRouter,
        context.routeIndex,
        currentRouteBounds,
        gap,
        effectiveMovableOrigIds
      );
    }

    shiftLinearSiblingStructuresRight(
      context.parentList,
      getStructureRange(context.parentList, context.parentIndex),
      gap,
      effectiveMovableOrigIds
    );

    context = context.parent as ListContext;
  }
}

export function applyHorizontalRemoveShift({
  rootSteps,
  targetList,
  startIndex,
  cursorX,
  gap = STRUCTURE_HORIZONTAL_GAP,
  routeGap = ROUTE_VERTICAL_GAP,
  movableOrigIds = null,
}: {
  rootSteps: any[];
  targetList: any[];
  startIndex: number;
  cursorX: number;
  gap?: number;
  routeGap?: number;
  movableOrigIds?: Set<string> | null;
}): void {
  shiftLinearSiblingStructuresLeft(
    targetList,
    startIndex,
    cursorX,
    gap,
    movableOrigIds
  );

  let context = findListContext(rootSteps, targetList);
  if (!context) return;

  while (context.parentRouter && context.parentList) {
    const currentRouteBounds = getScopedListBounds(
      context.list,
      movableOrigIds
    );

    if (currentRouteBounds) {
      shiftIndependentSiblingBranchesLeft(
        context.parentRouter,
        context.routeIndex,
        currentRouteBounds,
        gap,
        movableOrigIds
      );
      shiftRouteSiblingBranchesUp(
        context.parentRouter,
        context.routeIndex + 1,
        currentRouteBounds.maxY + routeGap,
        routeGap,
        movableOrigIds
      );
    }

    const parentRange = getStructureRange(
      context.parentList,
      context.parentIndex
    );
    const parentBounds = getStepsBounds(
      filterStepsByShiftScope(
        collectStructureSteps(context.parentList, parentRange),
        movableOrigIds
      )
    );

    if (parentBounds) {
      shiftLinearSiblingStructuresLeft(
        context.parentList,
        parentRange.endIndex + 1,
        parentBounds.maxX + gap,
        gap,
        movableOrigIds
      );
    }

    context = context.parent as ListContext;
  }
}

export function applyHorizontalShrinkShift({
  rootSteps,
  targetList,
  structureIndex,
  gap = STRUCTURE_HORIZONTAL_GAP,
  routeGap = ROUTE_VERTICAL_GAP,
  movableOrigIds = null,
}: {
  rootSteps: any[];
  targetList: any[];
  structureIndex: number;
  gap?: number;
  routeGap?: number;
  movableOrigIds?: Set<string> | null;
}): void {
  if (!Array.isArray(targetList) || structureIndex < 0) return;

  const structureRange = getStructureRange(targetList, structureIndex);
  const structureBounds = getStepsBounds(
    filterStepsByShiftScope(
      collectStructureSteps(targetList, structureRange),
      movableOrigIds
    )
  );

  if (!structureBounds) return;

  applyHorizontalRemoveShift({
    rootSteps,
    targetList,
    startIndex: structureRange.endIndex + 1,
    cursorX: structureBounds.maxX + gap,
    gap,
    routeGap,
    movableOrigIds,
  });
}

export function applyOrganizedSpacing(
  rootSteps: any[],
  gap = STRUCTURE_HORIZONTAL_GAP
): void {
  if (!Array.isArray(rootSteps) || rootSteps.length === 0) return;

  const alignList = (list: any[]) => {
    if (!Array.isArray(list) || list.length === 0) return;

    list.forEach((step) => {
      if (step?.type === "ROUTER" && Array.isArray(step.routes)) {
        step.routes.forEach((route: any) => alignList(route?.steps || []));
      }
    });

    let currentIndex = 0;
    while (currentIndex < list.length) {
      const structureRange = getStructureRange(list, currentIndex);
      applyHorizontalAddShift({
        insertedRange: structureRange,
        rootSteps,
        targetList: list,
        gap,
      });
      currentIndex = structureRange.endIndex + 1;
    }
  };

  alignList(rootSteps);
}

export function compactOrganizedSpacingByOrder(
  rootSteps: any[],
  gap = STRUCTURE_HORIZONTAL_GAP,
  movableOrigIds?: Set<string> | null
): void {
  if (!Array.isArray(rootSteps) || rootSteps.length === 0) return;

  const getScopedStructureSteps = (list: any[], range: StructureRange): any[] =>
    filterStepsByShiftScope(collectStructureSteps(list, range), movableOrigIds);

  const shiftStepsToX = (steps: any[], targetX: number): Bounds | null => {
    const structureBounds = getStepsBounds(steps);
    if (!structureBounds) return null;

    shiftStructureSteps(
      steps,
      targetX - structureBounds.minX,
      0,
      movableOrigIds
    );
    const nextBounds = getStepsBounds(steps);
    return nextBounds;
  };

  const compactRouterRoutes = (routerStep: any, routerX: number): void => {
    if (!routerStep || !Array.isArray(routerStep.routes)) return;

    routerStep.routes.forEach((route: any) => {
      compactList(route?.steps || [], routerX);
    });
  };

  const compactList = (list: any[], startX: number): number => {
    if (!Array.isArray(list) || list.length === 0) return startX;

    let cursorX = startX;
    let currentIndex = 0;

    while (currentIndex < list.length) {
      const structureRange = getStructureRange(list, currentIndex);

      if (structureRange.endIndex > currentIndex) {
        const loopStart = list[currentIndex];
        const loopEnd = list[structureRange.endIndex];
        const loopStartSteps = filterStepsByShiftScope(
          [loopStart],
          movableOrigIds
        );
        const loopStartBounds = shiftStepsToX(loopStartSteps, cursorX);
        let loopCursorX = loopStartBounds
          ? loopStartBounds.maxX + gap
          : cursorX;

        loopCursorX = compactList(
          list.slice(currentIndex + 1, structureRange.endIndex),
          loopCursorX
        );

        const loopEndSteps = filterStepsByShiftScope([loopEnd], movableOrigIds);
        const loopEndBounds = shiftStepsToX(loopEndSteps, loopCursorX);
        const loopSteps = getScopedStructureSteps(list, structureRange);
        const loopBounds = getStepsBounds(loopSteps);
        cursorX =
          (loopBounds?.maxX ?? loopEndBounds?.maxX ?? loopCursorX) + gap;
        currentIndex = structureRange.endIndex + 1;
      } else {
        const currentStep = list[currentIndex];
        const scopedStep = isStepInShiftScope(currentStep, movableOrigIds);

        if (scopedStep) {
          shiftStepsToX([currentStep], cursorX);
        }

        if (
          currentStep?.type === "ROUTER" &&
          Array.isArray(currentStep.routes)
        ) {
          const routerPosition = getStepPosition(currentStep);
          compactRouterRoutes(currentStep, routerPosition.x);
        }

        const structureSteps = getScopedStructureSteps(list, structureRange);
        const structureBounds = getStepsBounds(structureSteps);
        if (structureBounds) {
          cursorX = structureBounds.maxX + gap;
        }

        currentIndex = structureRange.endIndex + 1;
      }
    }

    return cursorX;
  };

  compactList(rootSteps, 0);
}

export function getRouteInsertionPoint(
  routerStep: any,
  afterRouteIndex: number,
  gap = ROUTE_VERTICAL_GAP
): { x: number; y: number } {
  const routerPosition = getStepPosition(routerStep);
  const previousRoute =
    Array.isArray(routerStep?.routes) && afterRouteIndex >= 0
      ? routerStep.routes[afterRouteIndex]
      : null;
  const previousBounds = getListBounds(previousRoute?.steps || []);

  return {
    x: routerPosition.x,
    y: previousBounds ? previousBounds.maxY + gap : routerPosition.y + gap,
  };
}

export function shiftRouteSiblingBranchesDown(
  routerStep: any,
  insertedRouteIndex: number,
  gap = ROUTE_VERTICAL_GAP,
  movableOrigIds?: Set<string> | null
): void {
  if (!routerStep || !Array.isArray(routerStep.routes)) return;

  const anchorRoute = routerStep.routes[insertedRouteIndex];
  const anchorBounds = getScopedListBounds(
    anchorRoute?.steps || [],
    movableOrigIds
  );
  if (!anchorBounds) return;

  let cursor = anchorBounds.maxY + gap;

  for (
    let routeIndex = insertedRouteIndex + 1;
    routeIndex < routerStep.routes.length;
    routeIndex += 1
  ) {
    const route = routerStep.routes[routeIndex];
    const routeBounds = getScopedListBounds(route?.steps || [], movableOrigIds);

    if (routeBounds && routeBounds.minY < cursor) {
      const deltaY = cursor - routeBounds.minY;
      shiftStructureSteps(
        getScopedRouteBranchSteps(route, movableOrigIds),
        0,
        deltaY,
        movableOrigIds
      );
      cursor = routeBounds.maxY + deltaY + gap;
    } else if (routeBounds) {
      cursor = routeBounds.maxY + gap;
    }
  }
}

export function shiftRouteSiblingBranchesUp(
  routerStep: any,
  startRouteIndex: number,
  cursorY: number,
  gap = ROUTE_VERTICAL_GAP,
  movableOrigIds?: Set<string> | null
): void {
  if (
    !routerStep ||
    !Array.isArray(routerStep.routes) ||
    startRouteIndex >= routerStep.routes.length
  ) {
    return;
  }

  let cursor = cursorY;

  for (
    let routeIndex = Math.max(0, startRouteIndex);
    routeIndex < routerStep.routes.length;
    routeIndex += 1
  ) {
    const route = routerStep.routes[routeIndex];
    const routeBounds = getScopedListBounds(route?.steps || [], movableOrigIds);

    if (routeBounds && routeBounds.minY > cursor) {
      const deltaY = cursor - routeBounds.minY;
      shiftStructureSteps(
        getScopedRouteBranchSteps(route, movableOrigIds),
        0,
        deltaY,
        movableOrigIds
      );
      cursor = routeBounds.maxY + deltaY + gap;
    } else if (routeBounds) {
      cursor = routeBounds.maxY + gap;
    }
  }
}
